/**
 * Мелкие примитивы интерфейса: карточка, чип, аватар, разделитель, деньги.
 *
 * Держим их в одном файле намеренно — это не «компоненты со сложной логикой»,
 * а словарь оформления. Разнесённые по десяти файлам, они начинают дублироваться.
 */

import clsx from 'clsx'
import type { HTMLAttributes, ReactNode } from 'react'

import { formatAmount, currencySymbol } from '@/shared/format'
import { Squiggle } from '@/shared/ui/decor'

/* ─── Карточка ──────────────────────────────────────────────────────────── */

export function Card({
  className,
  children,
  padded = true,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { padded?: boolean }) {
  // Свой фон ставим, только если вызывающий код не принёс собственный:
  // карточка смены и предупреждения красятся через className.
  const hasBackground = /(^|\s)bg-/.test(className ?? '')

  return (
    <div
      className={clsx(
        'sticker rounded-[var(--radius-card)]',
        !hasBackground && 'bg-surface',
        padded && 'p-[18px]',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  )
}

/**
 * Подпись раздела. Подчёркнута волнистой линией от руки: разделы отделяются
 * друг от друга, но в макет не добавляется ни одной серой линейки.
 */
export function SectionTitle({
  children,
  action,
  tone = 'accent',
}: {
  children: ReactNode
  action?: ReactNode
  tone?: 'accent' | 'warm' | 'honey' | 'muted'
}) {
  // Линия подчёркивает слово, поэтому обёртка ужимается по ширине заголовка,
  // а не растягивается на всю строку. Текст подписи заодно задаёт рисунок
  // линии: у «Ленты дня» и у «Смен» волны разные, но каждая своя постоянна.
  const seed = typeof children === 'string' ? children : ''

  return (
    <div className="flex items-end justify-between gap-3 px-1 pb-2.5">
      <span className="min-w-0">
        <span className="inline-block max-w-full">
          <h2 className="text-text text-caption font-semibold tracking-[0.08em] uppercase">
            {children}
          </h2>
          <Squiggle seed={seed} tone={tone} className="mt-1" />
        </span>
      </span>
      {action}
    </div>
  )
}

export function Divider({ className }: { className?: string }) {
  return <div className={clsx('bg-line h-px w-full', className)} />
}

/* ─── Статус-чип ────────────────────────────────────────────────────────── */

export type ChipTone = 'neutral' | 'accent' | 'warm' | 'success' | 'danger'

const CHIP_TONES: Record<ChipTone, string> = {
  neutral: 'bg-surface-muted text-text-muted',
  accent: 'bg-accent-soft text-accent',
  warm: 'bg-warm-soft text-warm-ink',
  success: 'bg-success-soft text-success',
  danger: 'bg-danger-soft text-danger',
}

export function Chip({
  tone = 'neutral',
  children,
  className,
  dot = false,
}: {
  tone?: ChipTone
  children: ReactNode
  className?: string
  dot?: boolean
}) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-[var(--radius-chip)] px-2.5 py-[3px]',
        'text-caption font-semibold tracking-[0.01em] whitespace-nowrap',
        CHIP_TONES[tone],
        className,
      )}
    >
      {dot && <span className="size-1.5 rounded-full bg-current" />}
      {children}
    </span>
  )
}

/* ─── Аватар ────────────────────────────────────────────────────────────── */

export function Avatar({
  name,
  src,
  size = 40,
  className,
}: {
  name: string
  src?: string | null
  size?: number
  className?: string
}) {
  const initials = name.trim().slice(0, 1).toUpperCase() || '?'
  return (
    <span
      style={{ width: size, height: size, fontSize: size * 0.4 }}
      className={clsx(
        'bg-accent-soft text-accent inline-flex shrink-0 items-center justify-center',
        'font-display overflow-hidden rounded-full',
        'ring-surface shadow-[var(--shadow-card)] ring-2',
        className,
      )}
    >
      {src ? (
        <img src={src} alt="" className="size-full object-cover" loading="lazy" />
      ) : (
        initials
      )}
    </span>
  )
}

/* ─── Деньги ────────────────────────────────────────────────────────────── */

/**
 * Единый компонент суммы (17.14). Крупные суммы — 34px табличными цифрами,
 * чтобы итог табеля читался как цифра в документе, а не как текст.
 */
export function Money({
  value,
  currency = 'RUB',
  size = 'md',
  tone,
  className,
}: {
  value: string | number | null | undefined
  currency?: string
  size?: 'sm' | 'md' | 'lg' | 'hero'
  tone?: 'default' | 'muted' | 'warm' | 'success' | 'danger'
  className?: string
}) {
  const amount = typeof value === 'number' ? value : Number(value ?? NaN)
  const negative = !Number.isNaN(amount) && amount < 0

  return (
    <span
      className={clsx(
        'tabular whitespace-nowrap',
        // герой — антиквой: итог периода должен читаться как сумма в документе
        size === 'hero' && 'font-display text-hero leading-none tracking-[-0.02em]',
        size === 'lg' && 'font-display text-title tracking-[-0.01em]',
        size === 'md' && 'text-body font-semibold',
        size === 'sm' && 'text-caption font-medium',
        tone === 'muted' && 'text-text-muted',
        tone === 'warm' && 'text-warm-ink',
        tone === 'success' && 'text-success',
        tone === 'danger' && 'text-danger',
        !tone && negative && 'text-text-muted',
        className,
      )}
    >
      {formatAmount(value)}
      <span className={clsx(size === 'hero' ? 'ml-1.5 text-title opacity-55' : 'ml-1 opacity-65')}>
        {currencySymbol(currency)}
      </span>
    </span>
  )
}

/* ─── Строка списка ─────────────────────────────────────────────────────── */

export function Row({
  icon,
  title,
  subtitle,
  right,
  onClick,
  className,
}: {
  icon?: ReactNode
  title: ReactNode
  subtitle?: ReactNode
  right?: ReactNode
  onClick?: () => void
  className?: string
}) {
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={clsx(
        'flex w-full items-center gap-3 py-3 text-left',
        onClick && 'press tap-target',
        className,
      )}
    >
      {icon}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] font-medium">{title}</span>
        {subtitle && (
          <span className="text-text-muted block truncate text-caption">{subtitle}</span>
        )}
      </span>
      {right}
    </Tag>
  )
}
